import { Device as CapDevice } from '@capacitor/device';
import { getCsrfToken, isCookieMode } from './apiService';
import { nuclearReset } from './appResetService';
import { JWT_STORAGE_KEY } from './authStorageKeys';
import { logInfo } from './loggerService';
import { CSRF_COOKIE_NAME } from './sessionCookieNames';

/**
 * `/logout` and `/reset`: escape hatches typed into the address bar when auth
 * is in a state the app can't get out of on its own — by the user, or by
 * support on a call.
 *
 * Both run from main.tsx INSTEAD of booting the app, so no gate (registration,
 * account status, update) can redirect them and no session restore can
 * re-write the cookies being cleared. Every step is best-effort: a failing or
 * unreachable BE must not stop the user getting out.
 *
 *   /logout — sign out of this app: the device is disassociated from the user,
 *             the BE clears this app's session cookies (access, refresh,
 *             csrf), and local + session storage are cleared except the device
 *             ID. Lands on `/` signed out.
 *   /reset  — everything /logout does, plus the device ID, every cookie this
 *             page can see, IndexedDB (Firebase's push token store) and the
 *             service workers + caches; reloads on a fresh copy of the app.
 *
 * Neither touches the admin session in the same browser.
 */

const LOGOUT_PATH = '/logout';
const RESET_PATH = '/reset';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const X_API_KEY = import.meta.env.VITE_X_API_KEY || '';
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;

// Where @capacitor/device keeps the web device ID. /logout keeps it so the BE
// goes on seeing the same device.
const DEVICE_ID_STORAGE_KEY = '_capuid';

// Admin's CSRF cookie is Domain-scoped, so this page can see it, and admin
// reads it as its "signed in" sentinel. /reset leaves it alone.
const ADMIN_CSRF_COOKIE_NAME = 'csrf_admin_token';

// Cap on each BE call and each IndexedDB delete, so nothing can hold the user
// on the splash screen.
const STEP_TIMEOUT_MS = 5000;

/**
 * Called first thing in main.tsx. Returns true when the path is `/logout` or
 * `/reset` and the exit has started; the caller must then not boot the app.
 * The page navigates away when the exit finishes.
 */
export function runSessionExitRoute(): boolean {
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
  if (path === LOGOUT_PATH) {
    void logout();
    return true;
  }
  if (path === RESET_PATH) {
    void reset();
    return true;
  }
  return false;
}

async function logout(): Promise<void> {
  logInfo('sessionExit: /logout');
  await signOutOnBackend();
  // The BE already cleared the CSRF cookie; this covers a BE that didn't
  // answer, so the app still boots signed out. The PostHog cookie carries the
  // identified distinct_id — the same reset logoutSoft does via posthog.reset().
  expireCookie(CSRF_COOKIE_NAME);
  if (POSTHOG_KEY) expireCookie(`ph_${POSTHOG_KEY}_posthog`);
  clearStorage([DEVICE_ID_STORAGE_KEY]);
  window.location.replace('/');
}

async function reset(): Promise<void> {
  logInfo('sessionExit: /reset');
  await signOutOnBackend();
  for (const name of visibleCookieNames()) {
    if (name !== ADMIN_CSRF_COOKIE_NAME) expireCookie(name);
  }
  clearStorage([]);
  await deleteIndexedDbs();
  try {
    await nuclearReset();
  } catch {
    window.location.replace('/');
  }
}

// Disassociate the device from the user first (it needs the session), then
// have the BE clear this app's session cookies. /auth/logout needs no valid
// session or CSRF token, so it works however broken the session is.
async function signOutOnBackend(): Promise<void> {
  try {
    const { identifier } = await CapDevice.getId();
    await postBestEffort('/device/logout', { identifier });
  } catch {
    /* best-effort */
  }
  await postBestEffort('/auth/logout', {});
}

// Plain fetch rather than apiService: its 401 refresh, logout redirect and
// error modal would all fight the exit.
async function postBestEffort(endpoint: string, body: unknown): Promise<void> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), STEP_TIMEOUT_MS);
  try {
    await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: buildHeaders(),
      body: JSON.stringify(body),
      credentials: 'include',
      signal: controller.signal,
    });
  } catch {
    /* best-effort: unreachable BE or timed out */
  } finally {
    window.clearTimeout(timer);
  }
}

function buildHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (X_API_KEY) headers['X-Api-Key'] = X_API_KEY;
  if (isCookieMode) {
    headers['X-Session-Mode'] = 'cookie';
    const csrf = getCsrfToken();
    if (csrf) headers['X-CSRF-Token'] = csrf;
  } else {
    const jwt = localStorage.getItem(JWT_STORAGE_KEY);
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  }
  return headers;
}

function visibleCookieNames(): string[] {
  return document.cookie
    .split(';')
    .map((c) => c.split('=')[0].trim())
    .filter(Boolean);
}

// Expire `name` host-only and on every parent domain of this host: page code
// can't read a cookie's Domain, and the BE sets the CSRF cookie on the env's
// parent domain (e.g. `.homeplated.com`). The browser ignores a Domain it
// wouldn't accept.
function expireCookie(name: string): void {
  const labels = window.location.hostname.split('.');
  document.cookie = `${name}=; Path=/; Max-Age=0`;
  for (let i = 0; i < labels.length - 1; i++) {
    document.cookie = `${name}=; Path=/; Max-Age=0; Domain=${labels.slice(i).join('.')}`;
  }
}

function clearStorage(keep: string[]): void {
  try {
    const kept = keep.map((key) => [key, localStorage.getItem(key)] as const);
    localStorage.clear();
    for (const [key, value] of kept) {
      if (value !== null) localStorage.setItem(key, value);
    }
  } catch {
    /* storage unavailable */
  }
  try {
    sessionStorage.clear();
  } catch {
    /* storage unavailable */
  }
}

// Each delete is capped: one stays pending while another tab holds that
// database open, and that must not hold up the reset.
async function deleteIndexedDbs(): Promise<void> {
  if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return;
  try {
    const dbs = await indexedDB.databases();
    await Promise.all(
      dbs.map(
        ({ name }) =>
          new Promise<void>((resolve) => {
            if (!name) return resolve();
            const timer = window.setTimeout(resolve, STEP_TIMEOUT_MS);
            const done = () => {
              window.clearTimeout(timer);
              resolve();
            };
            const request = indexedDB.deleteDatabase(name);
            request.onsuccess = done;
            request.onerror = done;
            request.onblocked = done;
          }),
      ),
    );
  } catch {
    /* best-effort */
  }
}
